import { Dispatch, SetStateAction } from "react";
import { GameState, RemoteNavigationState } from "../context/gameContextTypes";

// --- Menu Icon Navigation Helpers ---

/**
 * Moves remote highlight up in the in-game menu icon column.
 * @param setRemoteNavigation state setter for remote navigation
 * @param isAIGame if true, adjusts logic for extra Undo button
 */
export function moveRemoteUpInMenuIcons(
    setRemoteNavigation: Dispatch<SetStateAction<RemoteNavigationState>>,
    isAIGame: boolean,
) {
    setRemoteNavigation((prev) => {
        const options = isAIGame ? ["Options", "Undo"] : ["Options"]; // If AI game, show both
        const currentIndex = options.indexOf(prev.selectedMenuIcon ?? "");

        if (currentIndex <= 0) return { ...prev };

        const newMenuIcon = options[currentIndex - 1] as "Undo" | "Options";
        return { ...prev, selectedMenuIcon: newMenuIcon };
    });
}

/**
 * Moves remote highlight down in the in-game menu icon column.
 */
export function moveRemoteDownInMenuIcons(
    setRemoteNavigation: Dispatch<SetStateAction<RemoteNavigationState>>,
    isAIGame: boolean,
) {
    setRemoteNavigation((prev) => {
        const options = isAIGame ? ["Options", "Undo"] : ["Options"];
        const currentIndex = options.indexOf(prev.selectedMenuIcon ?? "");

        // If already at the bottom or not in the list, return unchanged
        if (currentIndex === -1 || currentIndex >= options.length - 1) return { ...prev };

        const newMenuIcon = options[currentIndex + 1] as "Undo" | "Options";
        return { ...prev, selectedMenuIcon: newMenuIcon };
    });
}

/**
 * Resets all remote navigation state (e.g., when exiting menu or view).
 */
export function leaveMenuIcons(
    setRemoteNavigation: Dispatch<SetStateAction<RemoteNavigationState>>,
) {
    setRemoteNavigation({
        isInMenuIcons: false,
        selectedMenuIcon: null,
        remoteClickSquare: null,
        remoteOverSquare: "h8",
        isInGameOverMenu: false,
        selectedGameOverIcon: null,
    });
}

/**
 * Toggles the in-game menu overlay visibility.
 */
export function toggleInGameMenu(setGameState: Dispatch<SetStateAction<GameState>>) {
    setGameState((prev) => ({ ...prev, showInGameMenu: !prev.showInGameMenu }));
}

// --- Game Over Menu Navigation ---

/**
 * Moves up between game-over menu options.
 */
export function moveRemoteUpInGameOverMenu(
    setRemoteNavigation: Dispatch<SetStateAction<RemoteNavigationState>>,
) {
    setRemoteNavigation((prev): RemoteNavigationState => {
        if (prev.selectedGameOverIcon === null) return { ...prev };
        const options: RemoteNavigationState["selectedGameOverIcon"][] = [
            "Play Again",
            "Main Menu",
        ];
        const currentIndex = options.indexOf(prev.selectedGameOverIcon);
        if (currentIndex <= 0) return { ...prev };
        const newIcon = options[currentIndex - 1];
        return { ...prev, selectedGameOverIcon: newIcon };
    });
}

/**
 * Moves down between game-over menu options.
 */
export function moveRemoteDownInGameOverMenu(
    setRemoteNavigation: Dispatch<SetStateAction<RemoteNavigationState>>,
) {
    setRemoteNavigation((prev) => {
        if (prev.selectedGameOverIcon === null) return { ...prev };
        const options: RemoteNavigationState["selectedGameOverIcon"][] = [
            "Play Again",
            "Main Menu",
        ];
        const currentIndex = options.indexOf(prev.selectedGameOverIcon);
        if (currentIndex === options.length - 1) return { ...prev };
        const newIcon = options[currentIndex + 1];
        return { ...prev, selectedGameOverIcon: newIcon };
    });
}

// --- Remote Navigation with Key Events ---

export let activeCol = 0;
export let activeRow = 0;

export const setActiveRow = (row: number) => {
    activeRow = row;
};
export const setActiveCol = (col: number) => {
    activeCol = col;
};

/**
 * Handles key navigation for elements using data-navcol and data-navrow attributes.
 */
export function handleKeyDown(event: KeyboardEvent) {
    event.preventDefault();
    if (window.location.hash) return;

    const elements = Array.from(document.querySelectorAll("[data-navcol][data-navrow]"));
    if (elements.length === 0) return;

    const maxRow = Math.max(...elements.map((el) => Number(el.getAttribute("data-navrow"))));
    const maxCol = Math.max(...elements.map((el) => Number(el.getAttribute("data-navcol"))));

    if (KeyCodes.Downs.includes(event.keyCode)) {
        if (activeRow < maxRow) {
            activeRow++;
            activeCol = 0;
        }
    } else if (KeyCodes.Ups.includes(event.keyCode)) {
        if (activeRow > 0) activeRow--;
    } else if (KeyCodes.Rights.includes(event.keyCode)) {
        if (activeCol < maxCol) activeCol++;
    } else if (KeyCodes.Lefts.includes(event.keyCode)) {
        if (activeCol > 0) activeCol--;
    } else if (event.keyCode === KeyCodes.Enter) {
        const current = elements.find(
            (el) =>
                Number(el.getAttribute("data-navrow")) === activeRow &&
                Number(el.getAttribute("data-navcol")) === activeCol,
        ) as HTMLElement;
        current?.click();
    }

    const active = elements.find(
        (el) =>
            Number(el.getAttribute("data-navrow")) === activeRow &&
            Number(el.getAttribute("data-navcol")) === activeCol,
    ) as HTMLElement;
    active?.focus();
}

// --- Key Code Constants ---

/**
 * Constants used for mapping key codes to navigation actions for Tv Remotes.
 */
export class KeyCodes {
    static Home = 36;
    static ArrowLeft = 37;
    static ArrowUp = 38;
    static ArrowRight = 39;
    static ArrowDown = 40;

    static w = 87;
    static a = 65;
    static s = 83;
    static d = 68;

    static Lefts = [KeyCodes.ArrowLeft, KeyCodes.a, "ArrowLeft"];
    static Ups = [KeyCodes.ArrowUp, KeyCodes.w, "ArrowUp"];
    static Rights = [KeyCodes.ArrowRight, KeyCodes.d, "ArrowRight"];
    static Downs = [KeyCodes.ArrowDown, KeyCodes.s, "ArrowDown"];

    static RightsDowns = KeyCodes.Rights.concat(KeyCodes.Downs);
    static LeftsUps = KeyCodes.Lefts.concat(KeyCodes.Ups);

    static Enter = 13;
    static Backspace = 461;
    static Escape = 27;
    static plus = 187;
    static samsung_tv_back = 10009;
    static sony_tv_back = 48;
    static Backspace_mac = 8;

    static Backspaces = [
        KeyCodes.Backspace_mac,
        KeyCodes.Backspace,
        KeyCodes.Escape,
        KeyCodes.plus,
        KeyCodes.samsung_tv_back,
        KeyCodes.sony_tv_back,
    ];
}
